import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import Toast, { ToastType } from '../components/organisms/Toast';
import ConfirmDialog, { ConfirmType } from '../components/organisms/ConfirmDialog';
import { registerToastMethods } from '../utilities/toastManager';

/**
 * Toast configuration interface
 */
interface ToastConfig {
  message: string;
  type?: ToastType;
  duration?: number;
  showIcon?: boolean;
  title?: string; // For backward compatibility with AlertDialog
  confirmText?: string; // For backward compatibility with AlertDialog (ignored in toast)
}

/**
 * Confirm configuration interface
 */
interface ConfirmConfig {
  title?: string;
  message: string;
  type?: ConfirmType;
  confirmText?: string;
  cancelText?: string;
  showIcon?: boolean;
  showCancel?: boolean; // New parameter to control cancel button visibility
}

/**
 * Toast context interface
 */
interface ToastContextType {
  showToast: (config: ToastConfig) => void;
  showAlert: (config: ToastConfig) => void; // Alias for backward compatibility
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showConfirm: (config: ConfirmConfig) => Promise<boolean>;
}

/**
 * Toast provider props
 */
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Individual toast state
 */
interface ToastState {
  id: string;
  open: boolean;
  message: string;
  type: ToastType;
  duration: number;
  showIcon: boolean;
  timeoutId?: NodeJS.Timeout; // Track the timeout for extending duration
}

/**
 * Confirm context state
 */
interface ConfirmState {
  open: boolean;
  title?: string;
  message: string;
  type: ConfirmType;
  confirmText: string;
  cancelText: string;
  showIcon: boolean;
  resolve?: (value: boolean) => void;
}

// Create the context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * ToastProvider component - Provides global toast and confirm functionality
 * 
 * This provider wraps the application and provides methods to show
 * toast notifications and confirmation dialogs throughout the app.
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    open: false,
    message: '',
    type: 'question',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showIcon: true,
  });

  /**
   * Generate unique ID for toasts
   */
  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  /**
   * Show a generic toast notification
   */
  const showToast = useCallback((config: ToastConfig) => {
    const message = config.message;
    const type = config.type || 'info';
    const duration = config.duration || 4000;
    
    // Check if an identical toast is already showing
    setToasts(prev => {
      const existingToastIndex = prev.findIndex(
        toast => toast.open && toast.message === message && toast.type === type
      );
      
      if (existingToastIndex !== -1) {
        // Toast with identical message and type already exists - extend its duration
        const updatedToasts = [...prev];
        const existingToast = updatedToasts[existingToastIndex];
        
        // Clear the existing timeout
        if (existingToast.timeoutId) {
          clearTimeout(existingToast.timeoutId);
        }
        
        // Set a new timeout with the extended duration
        const newTimeoutId = setTimeout(() => {
          removeToast(existingToast.id);
        }, duration);
        
        // Update the toast with new timeout
        updatedToasts[existingToastIndex] = {
          ...existingToast,
          timeoutId: newTimeoutId,
        };
        
        return updatedToasts;
      } else {
        // No identical toast exists - create a new one
        const id = generateId();
        const timeoutId = setTimeout(() => {
          removeToast(id);
        }, duration);
        
        const newToast: ToastState = {
          id,
          open: true,
          message: config.message,
          type: type,
          duration: duration,
          showIcon: config.showIcon !== false,
          timeoutId,
        };
        
        return [...prev, newToast];
      }
    });
  }, []);

  /**
   * Show a success toast
   */
  const showSuccess = useCallback((message: string, duration = 3000) => {
    showToast({
      message,
      type: 'success',
      duration,
    });
  }, [showToast]);

  /**
   * Show an error toast
   */
  const showError = useCallback((message: string, duration = 6000) => {
    showToast({
      message,
      type: 'error',
      duration,
    });
  }, [showToast]);

  /**
   * Show a warning toast
   */
  const showWarning = useCallback((message: string, duration = 5000) => {
    showToast({
      message,
      type: 'warning',
      duration,
    });
  }, [showToast]);

  /**
   * Show an info toast
   */
  const showInfo = useCallback((message: string, duration = 3000) => {
    showToast({
      message,
      type: 'info',
      duration,
    });
  }, [showToast]);

  /**
   * Show a confirmation dialog
   */
  const showConfirm = (config: ConfirmConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        open: true,
        title: config.title,
        message: config.message,
        type: config.type || 'question',
        confirmText: config.confirmText || 'Confirm',
        cancelText: config.showCancel === false ? '' : (config.cancelText || 'Cancel'),
        showIcon: config.showIcon !== false,
        resolve,
      });
    });
  };

  /**
   * Remove a toast by ID
   */
  const removeToast = (id: string) => {
    setToasts(prev => {
      const toastToRemove = prev.find(toast => toast.id === id);
      if (toastToRemove?.timeoutId) {
        clearTimeout(toastToRemove.timeoutId);
      }
      return prev.filter(toast => toast.id !== id);
    });
  };

  /**
   * Handle confirm dialog confirmation
   */
  const handleConfirm = () => {
    if (confirmState.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState(prev => ({
      ...prev,
      open: false,
      resolve: undefined,
    }));
  };

  /**
   * Handle confirm dialog cancellation
   */
  const handleCancel = () => {
    if (confirmState.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState(prev => ({
      ...prev,
      open: false,
      resolve: undefined,
    }));
  };

  const contextValue: ToastContextType = {
    showToast,
    showAlert: showToast, // Alias for backward compatibility
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
  };

  // Register toast methods for use in middleware
  useEffect(() => {
    registerToastMethods({
      showWarning,
      showInfo,
      showError,
      showSuccess,
    });
    
    // Cleanup function to clear all timeouts when component unmounts
    return () => {
      toasts.forEach(toast => {
        if (toast.timeoutId) {
          clearTimeout(toast.timeoutId);
        }
      });
    };
  }, [showError, showInfo, showSuccess, showWarning]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Render all active toasts */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={toast.open}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          showIcon={toast.showIcon}
          onClose={() => removeToast(toast.id)}
        />
      ))}
      
      {/* Render confirmation dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        showIcon={confirmState.showIcon}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ToastContext.Provider>
  );
};

/**
 * Custom hook to use the toast context
 * 
 * @returns Toast context methods
 * @throws Error if used outside of ToastProvider
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
